import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import ModuleLayout from '../components/ModuleLayout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Crown, Check, Loader2, Zap, Shield, Building2, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
const PLAN_ICONS = { trial: Sparkles, basic: Zap, pro: Crown, enterprise: Building2 };
const PLAN_COLORS = { trial: '#6B7280', basic: '#2563EB', pro: '#D97706', enterprise: '#7C3AED' };
export default function SubscriptionPage() {
  const { authHeaders, API } = useAuth();
  const { t } = useTranslation();
  const FEATURE_LABELS = {
    ai_chatbot: t('subscription.featureAiChatbot'), basic_analytics: t('subscription.featureBasicAnalytics'), advanced_analytics: t('subscription.featureAdvancedAnalytics'),
    website_builder: t('subscription.featureWebsiteBuilder'), voice_chat: t('subscription.featureVoiceChat'), priority_support: t('subscription.featurePrioritySupport'),
    custom_domain: t('subscription.featureCustomDomain'), api_access: t('subscription.featureApiAccess'), dedicated_support: t('subscription.featureDedicatedSupport'),
  };
  const [plans, setPlans] = useState([]);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [upgrading, setUpgrading] = useState(null);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [plansRes, statusRes] = await Promise.all([
        axios.get(`${API}/subscription/plans`, authHeaders()),
        axios.get(`${API}/subscription/status`, authHeaders()),
      ]);
      setPlans(plansRes.data);
      setStatus(statusRes.data);
    } catch { toast.error(t('subscription.loadFailed')); }
    finally { setLoading(false); }
  };

  const handleUpgrade = async (planId) => {
    if (planId === status?.plan_id) return;
    setUpgrading(planId);
    try {
      const res = await axios.post(`${API}/subscription/upgrade`, { plan: planId }, authHeaders());
      if (res.data.success) {
        toast.success(res.data.message);
        fetchData();
      }
    } catch { toast.error(t('subscription.upgradeFailed')); }
    finally { setUpgrading(null); }
  };

  if (loading) return (
    <ModuleLayout moduleKey="settings">
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    </ModuleLayout>
  );

  const currentPlan = status?.plan_id || 'trial';

  return (
    <ModuleLayout moduleKey="settings">
      <div className="space-y-6 max-w-5xl mx-auto" data-testid="subscription-page">
        <div className="text-center">
          <h1 className="font-bold text-2xl text-foreground flex items-center justify-center gap-2" style={{ fontFamily: 'Tajawal, sans-serif' }}>
            <Crown className="w-6 h-6 text-primary" /> {t('subscription.title')}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">{t('subscription.subtitle')}</p>
        </div>

        {/* Current Usage */}
        {status && (
          <Card className="border-primary/20 bg-primary/5">
            <CardContent className="p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Shield className="w-5 h-5 text-primary" />
                <div>
                  <p className="text-sm font-semibold">{t('subscription.currentPlanLabel')} <span className="text-primary">{status.plan?.name || t('subscription.trial')}</span></p>
                  <p className="text-xs text-muted-foreground">
                    {t('subscription.branchesLabel')} {status.usage?.branches}/{status.plan?.max_branches === -1 ? '∞' : status.plan?.max_branches} | 
                    {t('subscription.menuItemsLabel')} {status.usage?.menu_items}/{status.plan?.max_menu_items === -1 ? '∞' : status.plan?.max_menu_items}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Plans Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {plans.map(plan => {
            const Icon = PLAN_ICONS[plan.id] || Zap;
            const color = PLAN_COLORS[plan.id] || '#D97706';
            const isCurrent = currentPlan === plan.id;
            const isPopular = plan.id === 'pro';
            return (
              <Card key={plan.id}
                className={`relative overflow-hidden transition-all duration-300 ${
                  isCurrent ? 'ring-2 ring-primary shadow-lg' :
                  isPopular ? 'ring-1 ring-amber-500/30 shadow-lg shadow-amber-500/5 hover:shadow-xl hover:shadow-amber-500/10' :
                  'hover:shadow-md'
                }`}
                data-testid={`plan-${plan.id}`}
              >
                {/* Top accent */}
                <div className="h-1" style={{ background: `linear-gradient(90deg, ${color}, ${color}80)` }} />
                {isPopular && !isCurrent && (
                  <div className="absolute top-3 end-3">
                    <Badge className="text-[10px] bg-gradient-to-r from-amber-500 to-orange-600 text-white border-0">
                      {t('landing.mostPopular')}
                    </Badge>
                  </div>
                )}
                <CardContent className="p-5 space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${color}12` }}>
                      <Icon className="w-5 h-5" style={{ color }} />
                    </div>
                    <div>
                      <h3 className="font-bold text-foreground">{plan.name}</h3>
                      <p className="text-[11px] text-muted-foreground">{plan.name_en}</p>
                    </div>
                  </div>

                  <div className="py-2">
                    <span className="text-3xl font-extrabold text-foreground">{plan.price}</span>
                    <span className="text-sm text-muted-foreground mr-1">{t('subscription.pricePerMonth')}</span>
                  </div>

                  <div className="space-y-2.5 text-xs">
                    <div className="flex justify-between text-muted-foreground">
                      <span>{t('subscription.branchesLabel')}</span>
                      <span className="font-semibold text-foreground">{plan.max_branches === -1 ? t('subscription.unlimited') : plan.max_branches}</span>
                    </div>
                    <div className="flex justify-between text-muted-foreground">
                      <span>{t('subscription.menuItemsLabel')}</span>
                      <span className="font-semibold text-foreground">{plan.max_menu_items === -1 ? t('subscription.unlimited') : plan.max_menu_items}</span>
                    </div>
                    <div className="flex justify-between text-muted-foreground">
                      <span>{t('aiUsage.textRequests')}</span>
                      <span className="font-semibold text-foreground">{plan.ai_text_limit === -1 ? t('subscription.unlimited') : plan.ai_text_limit}</span>
                    </div>
                    <div className="flex justify-between text-muted-foreground">
                      <span>{t('aiUsage.imageRequests')}</span>
                      <span className="font-semibold text-foreground">{plan.ai_image_limit === -1 ? t('subscription.unlimited') : plan.ai_image_limit}</span>
                    </div>
                  </div>

                  <div className="space-y-2 pt-3 border-t border-border/50">
                    {plan.features?.map(f => (
                      <div key={f} className="flex items-center gap-2.5 text-xs">
                        <div className="w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${color}15` }}>
                          <Check className="w-2.5 h-2.5" style={{ color }} />
                        </div>
                        <span className="text-muted-foreground">{FEATURE_LABELS[f] || f}</span>
                      </div>
                    ))}
                  </div>

                  <Button
                    onClick={() => handleUpgrade(plan.id)}
                    disabled={isCurrent || upgrading === plan.id}
                    className={`w-full rounded-xl h-10 text-xs font-bold ${isPopular && !isCurrent ? 'bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white' : ''}`}
                    variant={isCurrent ? 'outline' : isPopular ? 'default' : 'default'}
                    data-testid={`upgrade-${plan.id}`}
                  >
                    {upgrading === plan.id ? <Loader2 className="w-4 h-4 animate-spin" /> :
                      isCurrent ? t('subscription.currentPlanBtn') : plan.price === 0 ? t('subscription.startFreeBtn') : t('subscription.upgradeBtn')}
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>

        <p className="text-center text-xs text-muted-foreground">
          {t('subscription.paymentNote')}
        </p>
      </div>
    </ModuleLayout>
  );
}
